// =======================================
// core/registry.js
// data-module / data-component を走査して起動するレジストリ
// - init() は何度呼んでも二重起動しない（要素ごとに初期化済みを保持）
// =======================================

/**
 * data属性の区切りを柔軟に扱う（スペース / カンマ）
 * 例: "header drawer" / "header,drawer"
 * @param {string} raw
 * @returns {string[]}
 */
function parseNames(raw) {
  if (!raw) return [];
  return raw
    .split(/[\s,]+/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * root 自身が selector に一致する場合も対象に含める
 * @param {ParentNode|HTMLElement|Document} root
 * @param {string} selector
 * @returns {HTMLElement[]}
 */
function queryAllIncludingRoot(root, selector) {
  /** @type {HTMLElement[]} */
  const list = [];

  if (root instanceof HTMLElement && root.matches(selector)) {
    list.push(root);
  }

  if (root && typeof root.querySelectorAll === "function") {
    list.push(
      ...Array.from(root.querySelectorAll(selector)).filter(
        (el) => el instanceof HTMLElement
      )
    );
  }

  // 重複排除
  return Array.from(new Set(list));
}


/**
 * 既に初期化済みの名前セットを取得
 * @param {HTMLElement} el
 * @param {string} key
 * @returns {Set<string>}
 */
function getInitedSet(el, key) {
  const raw = el.getAttribute(key) || "";
  return new Set(parseNames(raw));
}

/**
 * 初期化済みとして名前を保存
 * @param {HTMLElement} el
 * @param {string} key
 * @param {Set<string>} set
 */
function saveInitedSet(el, key, set) {
  el.setAttribute(key, Array.from(set).join(" "));
}

/**
 * モジュール/コンポーネントの登録領域
 */
const moduleFactories = new Map();
const componentFactories = new Map();

export const registry = {
  /**
   * module 登録
   * @param {string} name
   * @param {(ctx: {root: HTMLElement}) => (void|(() => void))} factory
   */
  registerModule(name, factory) {
    moduleFactories.set(name, factory);
  },

  /**
   * component 登録
   * @param {string} name
   * @param {(ctx: {el: HTMLElement}) => (void|(() => void))} factory
   */
  registerComponent(name, factory) {
    componentFactories.set(name, factory);
  },

  /**
   * data-module / data-component を走査して起動（※二重起動しない）
   * @param {ParentNode|HTMLElement|Document} root
   */
  init(root = document) {
    // --------------------------
    // modules
    // --------------------------
    const moduleHosts = queryAllIncludingRoot(root, "[data-module]");
    moduleHosts.forEach((host) => {
      const names = parseNames(host.getAttribute("data-module") || "");
      if (names.length === 0) return;

      // 初期化済みを data 属性に保持（front.js 等で再 init しても安全）
      const key = "data-pb-inited-modules";
      const inited = getInitedSet(host, key);

      names.forEach((name) => {
        if (inited.has(name)) return;

        const factory = moduleFactories.get(name);
        if (!factory) return;

        try {
          factory({ root: host });
          inited.add(name);
        } catch (err) {
          // 1つ失敗しても他は動かす
          console.error(`[PB] module init failed: ${name}`, err);
        }
      });

      saveInitedSet(host, key, inited);
    });

    // --------------------------
    // components
    // --------------------------
    const componentEls = queryAllIncludingRoot(root, "[data-component]");
    componentEls.forEach((el) => {
      const names = parseNames(el.getAttribute("data-component") || "");
      if (names.length === 0) return;

      const key = "data-pb-inited-components";
      const inited = getInitedSet(el, key);

      names.forEach((name) => {
        if (inited.has(name)) return;

        const factory = componentFactories.get(name);
        if (!factory) return;

        try {
          factory({ el });
          inited.add(name);
        } catch (err) {
          console.error(`[PB] component init failed: ${name}`, err);
        }
      });

      saveInitedSet(el, key, inited);
    });
  },
};
